import { useMemo } from 'react'

import { ArrowRight01Icon, CalendarFavorite02Icon } from '@hugeicons/react'
import { DateTime } from 'luxon'

import { useEventSlug } from '@/shared/hooks/useEventSlug'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useRecords } from '@/shared/hooks/useRecords'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'

import { BlockEmptyState } from '../BlockEmptyState'
import { BlockSkeleton } from '../BlockSkeleton'

import type { Session } from '../../../schedule/model/types'
import type { BlockComponentProps } from '../index'

const DEFAULT_LIMIT = 3

/**
 * Próximas sessões da programação.
 *
 * Não usa `useScheduleView`: aquele hook lê de `useResourceRecordsContext`, que
 * só existe sob o `withResourceRecords` da rota de programação. Na home o bloco
 * busca a coleção por conta própria, no mesmo endpoint que a rota usa.
 */
export const ScheduleBlock = ({ block }: BlockComponentProps) => {
  const eventSlug = useEventSlug()
  const { navigateTo } = useNavigation()

  const { records, isLoading } = useRecords({
    r: `/user/digital_products/${eventSlug}/programmings`,
    params: {
      pagination: { perPage: 999 },
      sort: { field: 'start_at', order: 'asc' }
    }
  })

  const upcoming = useMemo(() => {
    const now = DateTime.now()

    return ((records ?? []) as Session[])
      .filter(session => DateTime.fromISO(session.end_at) >= now)
      .sort(
        (a, b) =>
          DateTime.fromISO(a.start_at).toMillis() -
          DateTime.fromISO(b.start_at).toMillis()
      )
  }, [records])

  if (isLoading) return <BlockSkeleton rows={3} />

  if (upcoming.length === 0)
    return (
      <BlockEmptyState
        icon={CalendarFavorite02Icon}
        title="Nenhuma atividade a seguir"
        hint="As próximas sessões da programação aparecem aqui."
      />
    )

  const limit = block.settings?.limit ?? DEFAULT_LIMIT
  const sessions = upcoming.slice(0, limit)

  return (
    <Card className="card-gamification">
      <div className="flex items-center justify-between px-4 pb-0.5 pt-3">
        <h2 className="m-0 flex items-center gap-2 text-caps font-bold uppercase tracking-wide text-gray-700">
          <CalendarFavorite02Icon size={14} variant="duotone" aria-hidden />
          {block.title || 'Programação'}
        </h2>
        <Button
          variant="link"
          className="!no-underline text-xs font-bold"
          onClick={() => navigateTo('/programacao')}
          trailingIcon={<ArrowRight01Icon size={14} aria-hidden />}
        >
          Ver programação
        </Button>
      </div>

      <Card.Body className="flex flex-col gap-2 px-4 pb-3 pt-1.5">
        {sessions.map(session => (
          <button
            key={session.id}
            type="button"
            onClick={() => navigateTo('/programacao')}
            className="flex w-full cursor-pointer items-center gap-3 rounded-[0.625rem] border-0 bg-[color-mix(in_srgb,var(--color-brand)_8%,white)] px-3 py-2 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--color-brand)_12%,white)]"
          >
            <span className="flex shrink-0 flex-col items-center">
              <span className="text-caps font-bold uppercase tracking-wide text-gray-700">
                {DateTime.fromISO(session.start_at).toFormat('dd LLL')}
              </span>
              <span className="text-sm font-bold leading-tight text-[var(--color-brand)]">
                {DateTime.fromISO(session.start_at).toFormat('HH:mm')}
              </span>
            </span>

            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="truncate text-sm font-bold text-gray-900">
                {session.name}
              </span>
              {session.place?.name && (
                <span className="truncate text-xs font-semibold text-gray-700">
                  {session.place.name}
                </span>
              )}
            </span>

            <ArrowRight01Icon
              size={16}
              className="shrink-0 text-gray-600"
              aria-hidden
            />
          </button>
        ))}
      </Card.Body>
    </Card>
  )
}
